import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  Select,
  AutoComplete,
  Alert,
  message
} from 'antd';
import { api } from '../../utils/apiClient';
import { useUser } from '../../contexts/UserContext';

function AddUserDialog({ open, onClose, onUserAdded }) {
  const { user } = useUser();
  const [form] = Form.useForm();
  const [organizations, setOrganizations] = useState([]);
  const [roles, setRoles] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [currentOrgName, setCurrentOrgName] = useState('');
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [orgName, setOrgName] = useState('');
  const [loading, setLoading] = useState(false);

  // Check if user is SuperAdmin and fetch organizations
  useEffect(() => {
    if (open) {
      checkSuperAdmin();
      fetchOrganizations();
    }
  }, [open]);

  // Fetch roles when org_id changes (only for existing orgs)
  useEffect(() => {
    if (selectedOrgId && !orgName) {
      fetchRoles(selectedOrgId);
    } else if (orgName) {
      setRoles([]);
    }
  }, [selectedOrgId, orgName]);

  const checkSuperAdmin = async () => {
    try {
      const response = await api.get('/userprivileges');
      const privileges = response.data;
      const superAdmin = privileges.some(p => p.privilege_name === 'SuperAdmin');
      setIsSuperAdmin(superAdmin);

      if (!superAdmin && user?.org_id) {
        try {
          const orgResponse = await api.get(`/organizations/${user.org_id}`);
          setCurrentOrgName(orgResponse.data.name);
          setSelectedOrgId(user.org_id);
        } catch (orgError) {
          console.error('Failed to fetch org name:', orgError);
          setCurrentOrgName('Current Organization');
        }
      }
    } catch (error) {
      console.error('Failed to check SuperAdmin status:', error);
    }
  };

  const fetchOrganizations = async () => {
    try {
      const response = await api.get('/organizations');
      setOrganizations(response.data);
    } catch (error) {
      console.error('Failed to fetch organizations:', error);
    }
  };

  const fetchRoles = async (orgId) => {
    if (!orgId) return;

    try {
      const response = await api.get(`/roles?org_id=${orgId}`);
      setRoles(response.data || []);
    } catch (error) {
      console.error('Failed to fetch roles:', error);
      setRoles([]);
    }
  };

  const handleOrgSelect = (value) => {
    const org = organizations.find(o => o.id === value);
    if (org) {
      setSelectedOrgId(org.id);
      setOrgName('');
    }
  };

  const handleOrgSearch = (value) => {
    const matchingOrg = organizations.find(
      org => org.name.toLowerCase() === value.toLowerCase()
    );
    if (!matchingOrg && value) {
      setOrgName(value.trim());
      setSelectedOrgId('');
    } else if (matchingOrg) {
      setSelectedOrgId(matchingOrg.id);
      setOrgName('');
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      if (isSuperAdmin && !selectedOrgId && !orgName) {
        setErrorMessage('Please select or enter an organization name');
        return;
      }

      setLoading(true);

      const payload = {
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        password: values.password,
        role_ids: values.role_ids || []
      };

      if (orgName) {
        payload.org_name = orgName;
      } else {
        payload.org_id = selectedOrgId;
      }

      await api.post('/add_user', payload);

      message.success('User created successfully');
      form.resetFields();
      setSelectedOrgId('');
      setOrgName('');
      setErrorMessage('');
      onUserAdded();
      onClose();
    } catch (error) {
      if (error.errorFields) {
        // Form validation error
        return;
      }
      const errorMsg = error.response?.data?.error || 'Failed to create user. Please try again.';
      setErrorMessage(errorMsg);
      console.error('Add user error:', error.response || error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    form.resetFields();
    setSelectedOrgId('');
    setOrgName('');
    setErrorMessage('');
    onClose();
  };

  const orgOptions = organizations.map(org => ({
    value: org.id,
    label: org.name,
    desc: org.slug
  }));

  return (
    <Modal
      title="Add User"
      open={open}
      onCancel={handleClose}
      onOk={handleSubmit}
      okText="Add User"
      cancelText="Cancel"
      confirmLoading={loading}
      width={600}
    >
      {errorMessage && (
        <Alert
          message={errorMessage}
          type="error"
          closable
          onClose={() => setErrorMessage('')}
          style={{ marginBottom: 16 }}
        />
      )}

      <Form
        form={form}
        layout="vertical"
        initialValues={{
          firstName: '',
          lastName: '',
          email: '',
          password: '',
          confirmPassword: '',
          role_ids: []
        }}
      >
        <Form.Item
          label="First Name"
          name="firstName"
          rules={[
            { required: true, message: 'Please enter first name' },
            {
              pattern: /^[A-Za-z0-9_-]{1,30}$/,
              message: 'First Name must be 1-30 characters with no special characters'
            }
          ]}
        >
          <Input placeholder="Enter first name" />
        </Form.Item>

        <Form.Item
          label="Last Name"
          name="lastName"
          rules={[
            { required: true, message: 'Please enter last name' },
            {
              pattern: /^[A-Za-z0-9_-]{1,30}$/,
              message: 'Last Name must be 1-30 characters with no special characters'
            }
          ]}
        >
          <Input placeholder="Enter last name" />
        </Form.Item>

        <Form.Item
          label="Email Address"
          name="email"
          rules={[
            { required: true, message: 'Please enter email' },
            { type: 'email', message: 'Please enter a valid email address' }
          ]}
        >
          <Input placeholder="Enter email address" />
        </Form.Item>

        {isSuperAdmin ? (
          <Form.Item
            label="Organization"
            required
            help={orgName ? `Will create new organization: "${orgName}"` : 'Select from list or type new organization name'}
          >
            <AutoComplete
              options={orgOptions}
              onSelect={handleOrgSelect}
              onSearch={handleOrgSearch}
              placeholder="Select existing or type new org name"
              optionRender={(option) => (
                <div>
                  <div>{option.label}</div>
                  <div style={{ fontSize: '0.75rem', color: '#666' }}>
                    {option.desc}
                  </div>
                </div>
              )}
            />
          </Form.Item>
        ) : (
          <Form.Item label="Organization">
            <Input value={currentOrgName} disabled />
          </Form.Item>
        )}

        <Form.Item
          label="Roles (optional)"
          name="role_ids"
          help={orgName ? "New organization will get default roles (Admin, User, Viewer). Assign roles after creation." : ""}
        >
          <Select
            mode="multiple"
            placeholder={orgName ? "Default roles will be created" : "Select roles"}
            options={roles.map(r => ({ label: r.name, value: r.id }))}
            disabled={orgName !== ''}
          />
        </Form.Item>

        <Form.Item
          label="Password"
          name="password"
          rules={[
            { required: true, message: 'Please enter password' },
            {
              pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$&*]).{8,12}$/,
              message: 'Password must be 8-12 characters with at least one uppercase, one lowercase, one number, and one special character (!@#$&*)'
            }
          ]}
        >
          <Input.Password placeholder="Enter password" />
        </Form.Item>

        <Form.Item
          label="Confirm Password"
          name="confirmPassword"
          dependencies={['password']}
          rules={[
            { required: true, message: 'Please confirm password' },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('password') === value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error('Passwords do not match'));
              },
            }),
          ]}
        >
          <Input.Password placeholder="Confirm password" />
        </Form.Item>
      </Form>
    </Modal>
  );
}

export default AddUserDialog;
